import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import NewsCard from "@/components/base/NewsCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { comics } from "@/mocks/comics";
import { news } from "@/mocks/news";
import { matchComic, matchNews } from "@/utils/comic";

type Scope = "全部" | "漫画" | "资讯";

const hotKeywords = ["精灵", "妖怪", "魔法生物", "治愈", "森林", "完结"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [scope, setScope] = useState<Scope>("全部");

  useDocumentMeta({
    title: query ? `妖精漫画-搜索「${query}」` : "妖精漫画-站内搜索",
    description: "在妖精漫画中搜索妖精题材漫画作品与相关资讯，快速找到你想看的内容。",
    keywords: "妖精漫画搜索,漫画检索,资讯搜索,妖精题材",
  });

  useEffect(() => {
    setInput(query);
  }, [query]);

  const submit = (value: string) => {
    const q = value.trim();
    if (q) setSearchParams({ q });
    else setSearchParams({});
  };

  const comicResults = useMemo(
    () => (query ? comics.filter((c) => matchComic(c, query)) : []),
    [query]
  );
  const newsResults = useMemo(
    () => (query ? news.filter((n) => matchNews(n, query)) : []),
    [query]
  );

  const total = comicResults.length + newsResults.length;
  const showComics = scope === "全部" || scope === "漫画";
  const showNews = scope === "全部" || scope === "资讯";
  const recommended = comics.slice(0, 3);

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "搜索" }]} />
          <h1 className="mt-4 font-heading text-2xl text-foreground-950 md:text-4xl">站内搜索</h1>
          <p className="mt-3 text-sm text-foreground-600">输入关键词，搜索妖精题材漫画与相关资讯。</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
            className="mt-6 flex flex-col gap-3 sm:flex-row"
          >
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="试试搜索「精灵」「妖怪」「治愈」…"
                className="h-12 w-full rounded-full border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 rounded-full bg-primary-500 px-8 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-2-line"></i>
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门搜索：</span>
            {hotKeywords.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => submit(k)}
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        {query ? (
          <>
            <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between">
              <p className="text-sm text-foreground-600">
                关键词 <span className="text-primary-600">「{query}」</span> 共找到{" "}
                <span className="text-primary-600">{total}</span> 条结果
              </p>
              <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
                {(["全部", "漫画", "资讯"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScope(s)}
                    className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      scope === s
                        ? "bg-secondary-500 text-background-50"
                        : "text-foreground-600 hover:text-secondary-800"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {total === 0 ? (
              <div className="mt-8 flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </span>
                <p className="text-base text-foreground-800">没有找到与「{query}」相关的内容</p>
                <p className="text-sm text-foreground-500">换个关键词试试，或看看下面为你准备的推荐。</p>
              </div>
            ) : (
              <div className="mt-6 flex flex-col gap-10">
                {showComics && comicResults.length > 0 && (
                  <section>
                    <h2 className="mb-4 flex items-center gap-2 font-heading text-lg text-foreground-950">
                      <i className="ri-book-2-line text-primary-600"></i>
                      漫画作品（{comicResults.length}）
                    </h2>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {comicResults.map((comic) => (
                        <ComicCard key={comic.id} comic={comic} />
                      ))}
                    </div>
                  </section>
                )}

                {showNews && newsResults.length > 0 && (
                  <section>
                    <h2 className="mb-4 flex items-center gap-2 font-heading text-lg text-foreground-950">
                      <i className="ri-newspaper-line text-accent-500"></i>
                      相关资讯（{newsResults.length}）
                    </h2>
                    <div className="flex flex-col gap-4">
                      {newsResults.map((item) => (
                        <NewsCard key={item.id} item={item} variant="list" />
                      ))}
                    </div>
                  </section>
                )}

                {scope !== "全部" &&
                  (scope === "漫画" ? comicResults.length === 0 : newsResults.length === 0) && (
                    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center">
                      <p className="text-sm text-foreground-500">
                        当前筛选下没有结果，试试切换到「全部」查看。
                      </p>
                      <button
                        type="button"
                        onClick={() => setScope("全部")}
                        className="mt-2 rounded-md bg-primary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                      >
                        查看全部结果
                      </button>
                    </div>
                  )}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-search-2-line text-3xl"></i>
            </span>
            <p className="text-base text-foreground-800">输入关键词，开始探索妖精世界</p>
            <p className="text-sm text-foreground-500">可以搜索漫画标题、作者、分类或资讯内容。</p>
          </div>
        )}

        <div className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl text-foreground-950">
            <i className="ri-sparkling-line text-accent-500"></i>
            为你推荐
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recommended.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>

        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}